import AspectRatioOutlinedIcon from '@mui/icons-material/AspectRatioOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';
import { Stack, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import React, { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';

import { NewEmailFormModel } from '@/components/emailWindow/model/NewEmailForm.model.ts';
import { NewEmailHeaderStyles } from '@/components/emailWindow/styles/NewEmail.styles.ts';
import { useAnyAttachmentIsNotReady } from '@/components/emailWindow/util/useAnyAttachmentIsNotReady.util.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import {
  NEW_EMAIL_REDUCER_EVENT,
  NEW_EMAIL_VIEW_MODE,
  useNewEmailUpdateState,
  useNewEmailWindowMode,
} from '@/views/emails/context/NewEmails.ts';

type Props = {
  isCollapsed: boolean;
  isSendingEmail: boolean;
  onClose: () => void;
  onCollapseClick: () => void;
};

export const NewEmailHeader: React.FC<Props> = props => {
  const { isCollapsed, isSendingEmail, onClose, onCollapseClick } = props;
  const { t } = useTranslationWithPrefix('views.emails.newEmail.header');
  const anyAttachmentIsNotReady = useAnyAttachmentIsNotReady();

  const disabled = isSendingEmail || anyAttachmentIsNotReady;

  return (
    <Box sx={NewEmailHeaderStyles}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
        }}
      >
        <Button disabled={isCollapsed || disabled} endIcon={<SendOutlinedIcon />} type="submit">
          {t('button.send')}
        </Button>
      </Stack>
      <HeaderControls
        disabled={disabled}
        isCollapsed={isCollapsed}
        onClose={onClose}
        onCollapseClick={onCollapseClick}
      />
    </Box>
  );
};

type HeaderControlsWrapperProps = PropsWithChildren & {
  disabled: boolean;
  onClose: () => void;
};

type OnChangeViewModeFunction = { onChangeViewMode: (mode: NEW_EMAIL_VIEW_MODE) => () => void };

const HeaderControlsWrapper: React.FC<HeaderControlsWrapperProps> = ({
  children,
  disabled,
  onClose,
}) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.header.tooltip');

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
      }}
    >
      {children}
      <IconButton disabled={disabled} onClick={onClose}>
        <Tooltip title={t('close')}>
          <CloseOutlinedIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
        </Tooltip>
      </IconButton>
    </Stack>
  );
};

type HeaderControlsFloatingProps = {
  disabled: boolean;
  isCollapsed: boolean;
  onChangeViewMode: OnChangeViewModeFunction['onChangeViewMode'];
  onCollapseClick: () => void;
};

const HeaderControlsFloating: React.FC<HeaderControlsFloatingProps> = ({
  disabled,
  isCollapsed,
  onChangeViewMode,
  onCollapseClick,
}) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.header.tooltip');

  if (isCollapsed) {
    return (
      <>
        <IconButton disabled={disabled} onClick={onCollapseClick}>
          <Tooltip title={t('floating')}>
            <AspectRatioOutlinedIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
          </Tooltip>
        </IconButton>
        <IconButton disabled={disabled} onClick={onChangeViewMode(NEW_EMAIL_VIEW_MODE.DOCKED)}>
          <Tooltip title={t('dock')}>
            <CloseFullscreenIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
          </Tooltip>
        </IconButton>
      </>
    );
  }

  return (
    <>
      <IconButton disabled={disabled} onClick={onCollapseClick}>
        <Tooltip title={t('minimize')}>
          <BrandingWatermarkOutlinedIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
        </Tooltip>
      </IconButton>
      <IconButton disabled={disabled} onClick={onChangeViewMode(NEW_EMAIL_VIEW_MODE.DOCKED)}>
        <Tooltip title={t('dock')}>
          <CloseFullscreenIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
        </Tooltip>
      </IconButton>
    </>
  );
};

type HeaderControlsDockedProps = {
  disabled: boolean;
  onChangeViewMode: OnChangeViewModeFunction['onChangeViewMode'];
  onCollapseClick: () => void;
};

const HeaderControlsDocked: React.FC<HeaderControlsDockedProps> = ({
  disabled,
  onChangeViewMode,
  onCollapseClick,
}) => {
  const { t } = useTranslationWithPrefix('views.emails.newEmail.header.tooltip');

  return (
    <>
      <IconButton
        disabled={disabled}
        onClick={onChangeViewMode(NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW)}
      >
        <Tooltip title={t('floating')}>
          <SettingsOverscanIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
        </Tooltip>
      </IconButton>
      <IconButton disabled={disabled} onClick={onCollapseClick}>
        <Tooltip title={t('minimize')}>
          <BrandingWatermarkOutlinedIcon sx={{ color: themeColors.black, fontSize: '24px' }} />
        </Tooltip>
      </IconButton>
    </>
  );
};

type HeaderControlsProps = Omit<HeaderControlsWrapperProps, 'children'> &
  Omit<HeaderControlsFloatingProps, 'onChangeViewMode'>;

const HeaderControls: React.FC<HeaderControlsProps> = props => {
  const { disabled, isCollapsed, onClose, onCollapseClick } = props;
  const { getValues } = useFormContext<NewEmailFormModel>();
  const updateNewEmailState = useNewEmailUpdateState();
  const windowMode = useNewEmailWindowMode();

  const handleChangeViewMode: OnChangeViewModeFunction['onChangeViewMode'] = mode => () => {
    const form = getValues();
    updateNewEmailState({
      payload: {
        form,
        mode,
      },
      type: NEW_EMAIL_REDUCER_EVENT.CHANGE_WINDOW_MODE,
    });
  };

  if (windowMode === NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW) {
    return (
      <HeaderControlsWrapper disabled={disabled} onClose={onClose}>
        <HeaderControlsFloating
          disabled={disabled}
          isCollapsed={isCollapsed}
          onChangeViewMode={handleChangeViewMode}
          onCollapseClick={onCollapseClick}
        />
      </HeaderControlsWrapper>
    );
  }

  if (windowMode === NEW_EMAIL_VIEW_MODE.DOCKED) {
    return (
      <HeaderControlsWrapper disabled={disabled} onClose={onClose}>
        <HeaderControlsDocked
          disabled={disabled}
          onChangeViewMode={handleChangeViewMode}
          onCollapseClick={onCollapseClick}
        />
      </HeaderControlsWrapper>
    );
  }

  throw new Error(`Unknown window mode: [${windowMode}]`);
};
